/**
 * Chapter 09 blocks (SPEC §3.09, §4): the evidence ledger and the print
 * copies of every chart table.
 *
 * Ledger rules: local records get full rows (date/basis, source/technology,
 * stance, confidence, excerpt, ref); statewide and national records are a
 * compact table with OUTLINED chips — never local stance. In a report with no
 * dated local stance, context rows read "Not assessed" with their reason and
 * carry no stance word, glyph or confidence. Refs resolve by exact URL.
 */
import React from 'react';
import type { ReportTheme } from '../../../../../types/reportTheme';
import type { SiteRagReport } from '../../../../../types/siteRag';
import { roleColor } from '../../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../../lib/reportSectionHeading';
import { getSectionLabel } from '../../../../../config/reportSections';
import type { PsrLedgerRow } from '../../../../../lib/sentiment/publicSentimentMetaV2Contract';
import {
  PSR_SCOPE_SHORT_LABELS,
  psrDateBasisLabel,
  psrFormatDay,
  psrFunnel,
  psrLedgerGroups,
  psrReasonLabel,
  psrSourceLabel,
  psrTechLabel,
  psrTrendMonths,
  psrWindowIndexLabel,
  type PsrLedgerGroup,
  type PsrReportView,
} from '../../../../../lib/sentiment/psrReportView';
import {
  CoverageGlyph,
  PSR_APPARATUS,
  PSR_SERIF,
  PsrBlock,
  PsrGroupHeading,
  PsrNote,
  PsrTable,
  StanceChip,
  type PsrTableGroup,
} from './psrUi';
import { ArchiveFunnelTable, PsrRef, UnscoredReasonsTable } from './ResearchBlocks';
import { ScopeStanceTable, TechSourceTable, TechStanceTable } from './StanceBlocks';
import { SentimentTrendTable } from './TrendBlocks';

function DateCell({ theme, row }: { theme: ReportTheme; row: PsrLedgerRow }): React.ReactElement {
  const day = psrFormatDay(row.observedDate);
  const basis = day ? psrDateBasisLabel(row.dateBasis) : 'No source date';
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontWeight: 600, color: roleColor(theme, 'ink') }}>{day ?? 'Undated'}</span>
      {basis && <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.annotation, color: roleColor(theme, 'faint'), lineHeight: 1.35 }}>{basis}</span>}
      {row.afterScan && <span data-after-scan style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.annotation, fontWeight: 700, color: roleColor(theme, 'ink') }}>After the scan date</span>}
    </span>
  );
}

function reasonLine(theme: ReportTheme, row: PsrLedgerRow): React.ReactNode {
  if (!row.reasonCode) return null;
  return (
    <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.annotation, fontWeight: 500, color: roleColor(theme, 'faint'), lineHeight: 1.35 }} title={row.reasonNote}>
      {psrReasonLabel(row.reasonCode)}
    </span>
  );
}

export function EvidenceLedger({ theme, view, references, visibleRefIndices }: { theme: ReportTheme; view: PsrReportView; references: SiteRagReport['references']; visibleRefIndices?: ReadonlySet<number> }): React.ReactElement | null {
  if (view.state === 'coverage-only' || !view.v2.ledger) return null;
  const assessed = view.index !== undefined;
  const groups = psrLedgerGroups(view).map(group => ({ ...group, rows: group.rows.filter(row => {
    const index = (references ?? []).findIndex(reference => reference.url === row.url);
    return index < 0 || (references?.[index]?.qualification !== 'unavailable' && (!visibleRefIndices || visibleRefIndices.has(index + 1)));
  }) }));
  const coverageOf = (sourceId: string) => view.coverage.find(row => row.sourceId === sourceId)?.coverage;
  const shown = (g: PsrLedgerGroup) => (g.rows.length < g.total ? ` · ${g.rows.length} of ${g.total} shown` : '');
  const [localStance, localUnscored, state, country] = groups;

  const localRow = (row: PsrLedgerRow) => {
    const glyph = coverageOf(row.sourceId);
    return {
      key: `n${row.n}`,
      cells: [
        <DateCell key="d" theme={theme} row={row} />,
        <span key="s" style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: roleColor(theme, 'body') }}>
            {glyph && <span style={{ color: roleColor(theme, 'muted') }} title={`Coverage: ${glyph}`}><CoverageGlyph state={glyph} /></span>}
            <span>{psrSourceLabel(row.sourceId)}</span>
          </span>
          <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.annotation, color: roleColor(theme, 'faint') }}>{psrTechLabel(row.btmTech)}</span>
        </span>,
        row.status === 'local-stance' && row.stance !== undefined
          ? <StanceChip key="c" theme={theme} kind="local" stance={row.stance} />
          : <span key="c" style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}><StanceChip theme={theme} kind="unscored" />{reasonLine(theme, row)}</span>,
        row.status === 'local-stance' && row.confidence !== undefined ? row.confidence.toFixed(2) : <span key="k" style={{ color: roleColor(theme, 'faint') }}>—</span>,
        <span key="e" style={{ color: roleColor(theme, 'body') }}>{row.snippet}</span>,
        row.url ? <PsrRef key="r" theme={theme} references={references} url={row.url} /> : <span key="r" style={{ color: roleColor(theme, 'faint') }}>—</span>,
      ],
    };
  };
  const contextRow = (row: PsrLedgerRow) => ({
    key: `n${row.n}`,
    cells: [
      <DateCell key="d" theme={theme} row={row} />,
      <span key="m" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.annotation, color: roleColor(theme, 'muted') }}>
          {PSR_SCOPE_SHORT_LABELS[row.scope]} · {psrSourceLabel(row.sourceId)} · {psrTechLabel(row.btmTech)}
        </span>
        <span style={{ fontSize: 12, color: roleColor(theme, 'body') }}>{row.snippet}</span>
      </span>,
      assessed
        ? (row.status === 'context-stance' && row.stance !== undefined
          ? <StanceChip key="c" theme={theme} kind="context" stance={row.stance} />
          : <StanceChip key="c" theme={theme} kind="context-unscored" reasonCode={row.reasonCode ?? 'no-usable-assessment'} />)
        : <span key="c" style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}><StanceChip theme={theme} kind="not-assessed" />{reasonLine(theme, row)}</span>,
      row.url ? <PsrRef key="r" theme={theme} references={references} url={row.url} /> : <span key="r" style={{ color: roleColor(theme, 'faint') }}>—</span>,
    ],
  });

  const localGroups: PsrTableGroup[] = [
    assessed
      ? { key: 'local-stance', label: 'Local · assessed', detail: `${localStance!.total} · dated, explicit, site scope; the only records in the index${shown(localStance!)}`, rows: localStance!.rows.map(localRow) }
      : { key: 'local-stance', label: 'Local · assessed', detail: 'none established', note: 'No dated, attributable local statement was found. Nothing is scored, and nothing is counted as neutral.', rows: [] },
    {
      key: 'local-unscored', label: 'Local · unscored',
      detail: assessed ? `${localUnscored!.total} · site scope, left out of the index; the chip gives the reason${shown(localUnscored!)}` : `${localUnscored!.total} · kept as evidence; the reason is on each chip${shown(localUnscored!)}`,
      rows: localUnscored!.rows.map(localRow),
      ...(localUnscored!.total === 0 ? { note: 'No unscored local records.' } : {}),
    },
  ];
  const contextDetail = (g: PsrLedgerGroup) => (assessed ? `${g.total} · never local stance${shown(g)}` : `${g.total} · not local stance; not assessed for this report${shown(g)}`);
  const contextGroups: PsrTableGroup[] = [state!, country!].filter(g => g.total > 0).map(g => ({
    key: g.key, label: g.key === 'state' ? 'Statewide context' : 'National context', detail: contextDetail(g), rows: g.rows.map(contextRow),
  }));
  const omitted = view.v2.ledgerOmitted;
  return (
    <PsrBlock
      theme={theme}
      testId="psr-evidence-ledger"
      title="Evidence ledger"
      subtitle={assessed
        ? 'Grouped by scope and status · excerpts verbatim · refs link to the report’s sources'
        : 'Local records first; regional records follow as context, not assessed for this report'}
      partial={view.partial}
      caveatPosition="top"
      caveat="Each local source carries its coverage glyph; rows from partial sources are kept."
      exhibit={false}
    >
      <div data-testid="psr-ledger-legend" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px' }}>
        {assessed && (
          <>
            <StanceChip theme={theme} kind="local" stance={-1} />
            <StanceChip theme={theme} kind="local" stance={0} />
            <StanceChip theme={theme} kind="local" stance={1} />
          </>
        )}
        <StanceChip theme={theme} kind="unscored" />
        <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.annotation, fontWeight: 500, color: roleColor(theme, 'faint') }}>local</span>
        <span aria-hidden="true" style={{ width: 1, height: 14, background: roleColor(theme, 'rule'), flex: 'none' }} />
        {assessed ? <StanceChip theme={theme} kind="context" stance={1} /> : <StanceChip theme={theme} kind="not-assessed" />}
        <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.annotation, fontWeight: 500, color: roleColor(theme, 'faint') }}>
          {assessed ? 'context: outlined, never local stance' : 'context: no stance word, glyph or confidence'}
        </span>
      </div>
      <PsrTable
        theme={theme}
        testId="psr-ledger-local"
        firstColumnStrong={false}
        columns={[
          { header: 'Date / basis', width: 104 },
          { header: 'Source / technology', width: 140 },
          { header: 'Stance', width: 124 },
          { header: 'Conf.', align: 'right', numeric: true, width: 58 },
          { header: 'Excerpt' },
          { header: 'Ref', align: 'right', width: 52 },
        ]}
        groups={localGroups}
      />
      {contextGroups.length > 0 && (
        <PsrTable
          theme={theme}
          testId="psr-ledger-context"
          firstColumnStrong={false}
          columns={[
            { header: 'Date', width: 104 },
            { header: 'Scope · source · excerpt' },
            { header: 'Stance', width: 140 },
            { header: 'Ref', align: 'right', width: 52 },
          ]}
          groups={contextGroups}
        />
      )}
      {omitted && omitted.local + omitted.context > 0 && (
        <PsrNote theme={theme} testId="psr-ledger-omitted">
          {omitted.local + omitted.context} further {omitted.local + omitted.context === 1 ? 'record is' : 'records are'} not listed here ({omitted.local} local, {omitted.context} context); every record still counts in the tables and charts above.
        </PsrNote>
      )}
    </PsrBlock>
  );
}

/** Screen and print: every chart's table, numbered, with the chapter it belongs to. */
export function PrintTableTwins({ theme, view }: { theme: ReportTheme; view: PsrReportView }): React.ReactElement | null {
  if (view.state === 'coverage-only') return null;
  const assessed = view.index !== undefined;
  const label = (id: string) => getSectionLabel(id, 'public-sentiment');
  const research = 'Sentiment Research Detail';
  const tables: Array<{ key: string; title: string; render: (caption: React.ReactNode) => React.ReactNode }> = [];
  if (assessed) {
    tables.push({ key: 'tech', title: `Local stance by technology (${label('perBtmSentiment')})`, render: c => <TechStanceTable theme={theme} view={view} caption={c} /> });
    tables.push({ key: 'tech-source', title: `Assessed local statements of local records, by technology and source (${label('perBtmSentiment')})`, render: c => <TechSourceTable theme={theme} view={view} caption={c} /> });
    if (view.assessment) {
      const months = psrTrendMonths(view.assessment.monthly, view.scan?.fetchedAt, view.windowDays);
      if (months.some(m => m.index !== undefined)) {
        tables.push({ key: 'trend', title: `Monthly local index and statements (${label('trendAnalysis')}) · ${psrWindowIndexLabel(view.windowDays)} ${Math.round(view.index!)}`, render: c => <SentimentTrendTable theme={theme} months={months} caption={c} /> });
      }
    }
    tables.push({ key: 'scope', title: `Stance by scope (${research})`, render: c => <ScopeStanceTable theme={theme} view={view} caption={c} /> });
  }
  if (view.v2.unscoredByReason?.length) {
    tables.push({ key: 'reasons', title: `Unscored records by reason (${research})`, render: c => <UnscoredReasonsTable theme={theme} view={view} caption={c} /> });
  }
  (view.v2.municipalArchiveAudit ?? []).forEach((audit, i) => {
    tables.push({ key: `archive-${i}`, title: `Municipal archive read-through${(view.v2.municipalArchiveAudit?.length ?? 0) > 1 ? ` · ${audit.jurisdiction}` : ''} (${research})`, render: c => <ArchiveFunnelTable theme={theme} model={psrFunnel(audit)} caption={c} /> });
  });
  if (!tables.length) return null;
  return (
    <div data-testid="psr-print-table-twins" data-span-columns style={{ borderTop: `1px solid ${roleColor(theme, 'rule')}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PsrGroupHeading theme={theme} label="Chart tables · print copies" detail="the same values as the charts, for print and screen readers" />
      {tables.map((table, i) => (
        <div key={table.key} data-print-twin={table.key}>
          {table.render(`Table ${i + 1} · ${table.title}`)}
        </div>
      ))}
    </div>
  );
}

/** A one-line collection receipt (date + fingerprint) under the ledger. */
export function CollectionReceiptLine({ theme, view }: { theme: ReportTheme; view: PsrReportView }): React.ReactElement | null {
  const scan = view.scan;
  if (!scan) return null;
  const fp = typeof scan.queryFingerprint === 'string' && scan.queryFingerprint.length > 12
    ? `${scan.queryFingerprint.slice(0, 8)}…${scan.queryFingerprint.slice(-4)} (${scan.queryFingerprint.length} hex)` : scan.queryFingerprint;
  const rows = Array.isArray(scan.sourceCoverage) ? scan.sourceCoverage.length : 0;
  const unreported = Array.isArray(scan.unreportedSources) ? scan.unreportedSources.length : 0;
  return (
    <div data-testid="psr-collection-receipt" style={{ borderTop: `1px solid ${roleColor(theme, 'rule')}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 4, fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.annotation, color: roleColor(theme, 'muted') }}>
      <span>Collection date <span style={{ color: roleColor(theme, 'ink') }}>{scan.fetchedAt}</span>{'   '}Query receipt <span style={{ color: roleColor(theme, 'ink') }}>{fp}</span></span>
      <span style={{ color: roleColor(theme, 'faint') }}>{rows} coverage {rows === 1 ? 'row' : 'rows'} and {unreported} unreported {unreported === 1 ? 'attempt' : 'attempts'}, as in the source coverage matrix.</span>
    </div>
  );
}

/** Coverage-only chapter 09 lead: nothing was collected, so no ledger is drawn. */
export function NoRecordsNotice({ theme }: { theme: ReportTheme }): React.ReactElement {
  return (
    <div data-testid="psr-no-records" role="note" style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <PsrGroupHeading theme={theme} label="No records were collected" />
      <p style={{ margin: 0, fontFamily: PSR_SERIF, fontSize: 13, lineHeight: 1.6, color: roleColor(theme, 'body') }}>No ledger is drawn. The collection receipt below is the whole appendix.</p>
    </div>
  );
}
